import React, { useState, useContext, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { CheckIcon, GlobeIcon, XIcon, ClockIcon, PaperPlaneTiltIcon, DownloadSimple, UploadSimple, WarningCircle, CaretDown, CopyIcon } from "@phosphor-icons/react";
import { CircularProgress, Tabs, Tab, Tooltip, Fade } from "@mui/material";
import axios from "axios";
import dayjs from "dayjs";
import { toast } from "react-toastify";

import { toastOptions, constants } from "../../../assets/constants";
import { AuthContext } from "../../../contexts";
import { CustomCheckbox, CustomList, CustomSelect, CustomTag, CustomPageHeader, CustomModal } from "../../../ui";

import MonthTabLabel from "./MonthTabLabel";
import CsvImporter from "./CsvImporter";
import { buildDebitList, formatDebitListForCopy, getPaymentModeTag, getRowPaymentMode, isClientBillable as isBillableClient, needsMandateWarning } from "./billingPayment";

const REQUIRED_CSV_COLUMNS = ["engineCode", "montant_ttc"];

const YearSelector = React.memo(({ yearOptions, selectedYear, onYearChange, isLoading }) => (
    <CustomSelect options={yearOptions} value={yearOptions.find((option) => option.value === selectedYear)} onChange={onYearChange} disabled={isLoading} width={120} />
));

const BillingSelectionContext = React.createContext(null);

const RowSelectCheckbox = ({ client }) => {
    const ctx = React.useContext(BillingSelectionContext);
    if (!ctx) return null;
    return (
        <CustomCheckbox
            checked={ctx.selectedIds.has(client.id)}
            disabled={!ctx.isClientBillable(client)}
            onChange={() => ctx.toggleClient(client.id)}
            ariaLabel="Sélectionner l'hôtel"
        />
    );
};

const HeaderSelectCheckbox = () => {
    const ctx = React.useContext(BillingSelectionContext);
    if (!ctx) return null;
    return (
        <CustomCheckbox
            checked={ctx.allEligibleSelected}
            indeterminate={ctx.someEligibleSelected && !ctx.allEligibleSelected}
            disabled={ctx.eligibleCount === 0}
            onChange={ctx.toggleSelectAll}
            ariaLabel="Sélectionner tous les hôtels facturables"
        />
    );
};

const Billing = () => {
    const [data, setData] = useState([]);
    const [isLoaded, setIsLoaded] = useState(false);
    const [refreshData, setRefreshData] = useState(0);
    const { token } = useContext(AuthContext);
    const [selectedYear, setSelectedYear] = useState(dayjs().year());
    const [selectedMonth, setSelectedMonth] = useState(dayjs().subtract(1, "month").month());
    const [yearOptions] = useState(() => {
        const currentYear = dayjs().year();
        return Array.from({ length: 5 }, (_, i) => ({
            label: (currentYear - i).toString(),
            value: currentYear - i,
        }));
    });
    const [monthlyInvoiceCounts, setMonthlyInvoiceCounts] = useState(null);
    const [yearlyInvoiceStats, setYearlyInvoiceStats] = useState(null);

    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [recapModalOpen, setRecapModalOpen] = useState(false);
    const [batchStatus, setBatchStatus] = useState({});
    const [batchClients, setBatchClients] = useState([]);
    const [isBatchRunning, setIsBatchRunning] = useState(false);
    const [batchDone, setBatchDone] = useState(false);
    const batchLockRef = React.useRef(false);

    const [importPreviewOpen, setImportPreviewOpen] = useState(false);
    const [importPreviewData, setImportPreviewData] = useState(null);
    const [isImporting, setIsImporting] = useState(false);
    const [isImportExpanded, setIsImportExpanded] = useState(true);

    const consumptionMonth = useMemo(
        () => dayjs().year(selectedYear).month(selectedMonth),
        [selectedYear, selectedMonth]
    );
    const billingMonth = useMemo(() => consumptionMonth.add(1, "month"), [consumptionMonth]);

    const billingStatus = useMemo(() => {
        const now = dayjs();
        if (billingMonth.isBefore(now, "month")) return "expired";
        if (billingMonth.isSame(now, "month")) return "billable";
        return "not_yet_billable";
    }, [billingMonth]);

    const isBillable = billingStatus === "billable";
    const isExpired = billingStatus === "expired";
    const isNotYetBillable = billingStatus === "not_yet_billable";

    const formatAmount = useCallback((amount, digits = 0) => {
        return Number(amount).toLocaleString("fr-FR", {
            maximumFractionDigits: digits,
            minimumFractionDigits: digits,
        });
    }, []);

    const isClientBillable = useCallback((client) => isBillableClient(client, isBillable), [isBillable]);

    const eligibleClients = useMemo(() => data.filter(isClientBillable), [data, isClientBillable]);

    const selectedClients = useMemo(() => data.filter((client) => selectedIds.has(client.id)), [data, selectedIds]);

    const selectedTotal = useMemo(
        () => selectedClients.reduce((total, client) => total + (Number(client.billingCost) || 0), 0),
        [selectedClients]
    );

    const allEligibleSelected = eligibleClients.length > 0 && eligibleClients.every((client) => selectedIds.has(client.id));
    const someEligibleSelected = eligibleClients.some((client) => selectedIds.has(client.id));

    const toggleClient = useCallback((clientId) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(clientId)) next.delete(clientId);
            else next.add(clientId);
            return next;
        });
    }, []);

    const toggleSelectAll = useCallback(() => {
        setSelectedIds((prev) => {
            const allSelected = eligibleClients.length > 0 && eligibleClients.every((client) => prev.has(client.id));
            if (allSelected) return new Set();
            return new Set(eligibleClients.map((client) => client.id));
        });
    }, [eligibleClients]);

    const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

    const showProgress = isBatchRunning || batchDone;

    const doneCount = useMemo(
        () => batchClients.filter((client) => ["success", "error"].includes(batchStatus[client.id]?.status)).length,
        [batchClients, batchStatus]
    );

    const closeRecap = useCallback(() => {
        if (isBatchRunning) return;
        if (batchDone) {
            setRefreshData((prev) => prev + 1);
            clearSelection();
        }
        setRecapModalOpen(false);
    }, [isBatchRunning, batchDone, clearSelection]);

    const openRecap = useCallback(() => {
        setBatchStatus({});
        setBatchClients([]);
        setBatchDone(false);
        setRecapModalOpen(true);
    }, []);

    const handleBatchInvoice = useCallback(async () => {
        if (batchLockRef.current) return;
        const clients = data.filter((client) => selectedIds.has(client.id));
        if (clients.length === 0) return;

        batchLockRef.current = true;
        setBatchClients(clients);
        setIsBatchRunning(true);
        setBatchDone(false);
        setBatchStatus(clients.reduce((acc, client) => ({ ...acc, [client.id]: { status: "pending" } }), {}));

        const billingDate = dayjs().format("YYYY-MM-DD");
        const consumptionMonthStr = dayjs().year(selectedYear).month(selectedMonth).format("YYYY-MM");

        let successCount = 0;
        let errorCount = 0;

        try {
            for (const client of clients) {
                setBatchStatus((prev) => ({ ...prev, [client.id]: { status: "processing" } }));
                try {
                    const response = await axios.post(
                        `${process.env.REACT_APP_UPA_API_HOST}metas/create-invoice`,
                        { ...client, billingDate, consumptionMonth: consumptionMonthStr },
                        { headers: { Authorization: `Bearer ${token}` } }
                    );
                    setBatchStatus((prev) => ({ ...prev, [client.id]: { status: "success", result: response.data } }));
                    successCount += 1;
                } catch (error) {
                    const reason = error.response?.data?.error || error.response?.data?.message || error.message || "Erreur inconnue";
                    setBatchStatus((prev) => ({ ...prev, [client.id]: { status: "error", error: reason } }));
                    errorCount += 1;
                }
            }
        } finally {
            setIsBatchRunning(false);
            setBatchDone(true);
            batchLockRef.current = false;
        }

        if (errorCount === 0) {
            toast.success(`${successCount} facture(s) créée(s) avec succès`, toastOptions);
        } else {
            toast.error(`${successCount} facture(s) créée(s), ${errorCount} échec(s)`, toastOptions);
        }
    }, [data, selectedIds, selectedYear, selectedMonth, token]);

    const [currentSortBy, setCurrentSortBy] = useState([{ id: "hotel", desc: false }]);
    const [totalMetaClientsCount, setTotalMetaClientsCount] = useState(0);
    const [isDataLoading, setIsDataLoading] = useState(false);
    const [isMonthLoading, setIsMonthLoading] = useState(false);

    const apiHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

    const fetchMonthData = useCallback(
        async (year, month) => {
            if (!token) return;
            const monthString = dayjs().year(year).month(month).format("YYYY-MM");
            const response = await axios.get(`${process.env.REACT_APP_UPA_API_HOST}metas/billing?month=${monthString}`, {
                headers: apiHeaders,
            });
            setData(response.data);
        },
        [token, apiHeaders]
    );

    const fetchYearlyInvoiceStats = useCallback(
        async (year) => {
            if (!token) return;
            const response = await axios.get(`${process.env.REACT_APP_UPA_API_HOST}metas/yearly-costs/${year}`, {
                headers: apiHeaders,
            });
            setYearlyInvoiceStats(response.data);
            const counts = {};
            response.data.monthly.forEach((monthData) => {
                counts[monthData.month - 1] = monthData.invoice_count;
            });
            setMonthlyInvoiceCounts(counts);
        },
        [token, apiHeaders]
    );

    useEffect(() => {
        if (!token) return;
        setIsMonthLoading(true);

        Promise.all([
            fetchMonthData(selectedYear, selectedMonth),
            fetchYearlyInvoiceStats(selectedYear),
            axios.get(`${process.env.REACT_APP_UPA_API_HOST}metas/customers/count`, { headers: apiHeaders }),
        ])
            .then(([, , countRes]) => setTotalMetaClientsCount(countRes.data.count))
            .catch((error) => toast.error(error.message, toastOptions))
            .finally(() => {
                setIsLoaded(true);
                setIsDataLoading(false);
                setIsMonthLoading(false);
            });
    }, []);

    const isInitialMount = React.useRef(true);
    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }
        if (!token) return;
        clearSelection();
        setIsMonthLoading(true);
        fetchMonthData(selectedYear, selectedMonth)
            .catch((error) => toast.error(error.message, toastOptions))
            .finally(() => {
                setIsDataLoading(false);
                setIsMonthLoading(false);
            });
    }, [selectedMonth]);

    const prevYear = React.useRef(selectedYear);
    useEffect(() => {
        if (prevYear.current === selectedYear) return;
        prevYear.current = selectedYear;
        if (!token) return;
        clearSelection();
        setIsMonthLoading(true);

        Promise.all([
            fetchMonthData(selectedYear, selectedMonth),
            fetchYearlyInvoiceStats(selectedYear),
        ])
            .catch((error) => toast.error(error.message, toastOptions))
            .finally(() => {
                setIsDataLoading(false);
                setIsMonthLoading(false);
            });
    }, [selectedYear]);

    const prevRefresh = React.useRef(refreshData);
    useEffect(() => {
        if (prevRefresh.current === refreshData) return;
        prevRefresh.current = refreshData;
        if (!token) return;
        setIsMonthLoading(true);

        Promise.all([
            fetchMonthData(selectedYear, selectedMonth),
            fetchYearlyInvoiceStats(selectedYear),
        ])
            .catch((error) => toast.error(error.message, toastOptions))
            .finally(() => {
                setIsDataLoading(false);
                setIsMonthLoading(false);
            });
    }, [refreshData]);

    const handleYearChange = useCallback((selectedOption) => {
        setIsDataLoading(true);
        setSelectedYear(selectedOption.value);
    }, []);

    const handleMonthChange = useCallback((_, newValue) => {
        setSelectedMonth(newValue);
    }, []);

    const handleDownloadTemplate = useCallback(async () => {
        const monthString = consumptionMonth.format("YYYY-MM");
        try {
            const response = await axios.get(`${process.env.REACT_APP_UPA_API_HOST}metas/billing/template?month=${monthString}`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: "blob",
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `facturation-metas-${monthString}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (_error) {
            toast.error("Erreur lors du téléchargement du template", toastOptions);
        }
    }, [token, consumptionMonth]);

    const normalizeEngineCode = useCallback((code) => {
        const num = parseInt(String(code || "").trim(), 10);
        return isNaN(num) ? String(code || "").trim() : String(num);
    }, []);

    const handleCsvImport = useCallback(
        (csvData) => {
            const engineCodesInData = new Map();
            data.forEach((row) => {
                if (row.engineCode) {
                    engineCodesInData.set(normalizeEngineCode(row.engineCode), row);
                }
            });

            const matched = [];
            const unmatched = [];
            const emptyAmount = [];

            csvData.forEach((row) => {
                const rawEngineCode = String(row.engineCode || "").trim();
                const normalized = normalizeEngineCode(rawEngineCode);
                const amountStr = String(row.montant_ttc || "").trim().replace(",", ".");
                const amount = parseFloat(amountStr);

                if (!normalized) return;

                const existingClient = engineCodesInData.get(normalized);

                if (!existingClient) {
                    unmatched.push({ engineCode: rawEngineCode, hotel: row.hotel || "-", amountTTC: isNaN(amount) ? 0 : amount });
                    return;
                }

                const originalEngineCode = existingClient.engineCode;

                if (!amountStr || isNaN(amount) || amount <= 0) {
                    emptyAmount.push({
                        engineCode: originalEngineCode,
                        hotel: existingClient.contact?.data?.customer_name || row.hotel || "-",
                        amountTTC: 0,
                    });
                    return;
                }

                matched.push({
                    engineCode: originalEngineCode,
                    hotel: existingClient.contact?.data?.customer_name || row.hotel || "-",
                    amountTTC: amount,
                });
            });

            const normalizedCsvCodes = new Set(csvData.map((csv) => normalizeEngineCode(csv.engineCode)));
            const missingFromCsv = data.filter((row) => row.engineCode && !normalizedCsvCodes.has(normalizeEngineCode(row.engineCode))).map((row) => ({
                engineCode: row.engineCode,
                hotel: row.contact?.data?.customer_name || "-",
            }));

            setImportPreviewData({ matched, unmatched, emptyAmount, missingFromCsv });
            setImportPreviewOpen(true);
        },
        [data, normalizeEngineCode]
    );

    const handleConfirmImport = useCallback(async () => {
        if (!importPreviewData?.matched?.length) return;

        setIsImporting(true);
        const monthString = consumptionMonth.format("YYYY-MM");

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_UPA_API_HOST}metas/billing/bulk`,
                {
                    month: monthString,
                    costs: importPreviewData.matched.map((item) => ({
                        engineCode: item.engineCode,
                        amountTTC: item.amountTTC,
                    })),
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            toast.success(`Import réussi : ${response.data.success} montants importés`, toastOptions);
            setImportPreviewOpen(false);
            setImportPreviewData(null);
            setRefreshData((prev) => prev + 1);
        } catch (_error) {
            toast.error("Erreur lors de l'import des montants", toastOptions);
        } finally {
            setIsImporting(false);
        }
    }, [importPreviewData, consumptionMonth, token]);

    const columns = useMemo(() => {
        return [
            {
                // "selection" gets the fixed checkbox width and is never wrapped in the row link (CustomList)
                id: "selection",
                accessor: "id",
                enableSorting: false,
                Header: <HeaderSelectCheckbox />,
                Cell: (row) => <RowSelectCheckbox client={row.row.original} />,
            },
            {
                Header: "Nom de l'hôtel",
                id: "hotel",
                icon: "user",
                accessor: "contact",
                Cell: (row) => (
                    <Tooltip
                        arrow
                        slots={{ transition: Fade }}
                        title={
                            row.row.original.engineCode && (
                                <CustomTag
                                    className={`${row.row.original.engineType || ""} uppercase`}
                                    value={`${row.row.original.engineCode || "Non configuré"} - ${row.row.original.engineType || "Type inconnu"}`}
                                />
                            )
                        }
                    >
                        <span>
                            <Link to={`/admin/inventory/customers/${row.row.original.pennylaneId}`}>{row.row.original.contact?.data?.customer_name}</Link>
                        </span>
                    </Tooltip>
                ),
            },
            {
                Header: "Taxe appliquée",
                id: "tax",
                icon: "tag-simple",
                accessor: "contact",
                Cell: (row) => <CustomTag value={row.row.original.taxId} />,
            },
            {
                Header: "Mode de paiement",
                id: "paymentMethod",
                icon: "euro",
                accessor: "paymentMethod",
                Cell: (row) => {
                    const client = row.row.original;
                    const mode = getRowPaymentMode(client);
                    const tag = <CustomTag className={mode.className} value={mode.label} />;

                    return (
                        <span className="billing-payment-mode">
                            {mode.invoiceNumber ? (
                                <Tooltip title={`Facture ${mode.invoiceNumber}`} arrow>
                                    <span>{tag}</span>
                                </Tooltip>
                            ) : (
                                tag
                            )}
                            {needsMandateWarning(client) && (
                                <Tooltip title="Mandat Pennylane non valide : le prélèvement ne pourra pas être programmé" arrow>
                                    <WarningCircle size={18} weight="bold" className="billing-payment-mode__warning" />
                                </Tooltip>
                            )}
                        </span>
                    );
                },
            },
            {
                Header: "Réservations",
                accessor: "bookingsAmount",
                icon: "calendar-check",
                Cell: (row) => <CustomTag value={formatAmount(row.row.original.bookingsAmount) + " €"} />,
            },
            {
                Header: "Réservations métas",
                icon: "calendar-check",
                accessor: "metasAmount",
                Cell: (row) => <CustomTag className="purple" value={formatAmount(row.row.original.metasAmount) + " € (" + parseInt(row.row.original.metaPercentage) + "%)"} />,
            },
            {
                Header: "Estimation",
                icon: "percentage",
                accessor: "estimatedMinAmount",
                Cell: (row) => <CustomTag value={formatAmount(row.row.original.estimatedMinAmount) + " - " + formatAmount(row.row.original.estimatedMaxAmount) + " €"} />,
            },
            {
                Header: "Montant à facturer TTC",
                id: "billing",
                icon: "euro",
                accessor: "billingCost",
                Cell: (row) => {
                    const hasInvoice = row.row.original.hasInvoice;
                    const amountTTC = row.row.original.amountTTC;
                    const currentValue = row.row.original.billingCost || 0;

                    if (hasInvoice) {
                        const amountHT = row.row.original.amountHT || 0;
                        const tooltipText = `Montant HT : ${formatAmount(amountHT, 2)} €`;
                        return (
                            <Tooltip title={tooltipText} arrow slots={{ transition: Fade }} className="custom-tooltip">
                                <span>
                                    <CustomTag className="credit" value={formatAmount(amountTTC, 2) + " €"} />
                                </span>
                            </Tooltip>
                        );
                    }

                    if (currentValue > 0) {
                        return <CustomTag className="waiting" value={formatAmount(currentValue, 2) + " €"} />;
                    }

                    return <CustomTag className="deactivated" value="-" />;
                },
            },
            {
                Header: "Statut",
                id: "status",
                icon: "none",
                accessor: "hasInvoice",
                Cell: ({ row }) => {
                    const client = row.original;

                    if (isExpired) {
                        return client.hasInvoice ? <CustomTag className="credit" value="Facturé" /> : <CustomTag className="deactivated" value="Aucune facture" />;
                    }
                    if (isNotYetBillable) {
                        return <CustomTag className="deactivated" value="À venir" />;
                    }
                    if (client.hasInvoice) {
                        return <CustomTag className="credit" value="Facturé" />;
                    }
                    if (isClientBillable(client)) {
                        return <CustomTag className="waiting" value="Prêt à facturer" />;
                    }
                    return <CustomTag className="deactivated" value="Montant manquant" />;
                },
            },
        ];
    }, [isClientBillable, formatAmount, isExpired, isNotYetBillable]);

    const selectionContextValue = useMemo(
        () => ({
            selectedIds,
            toggleClient,
            toggleSelectAll,
            isClientBillable,
            allEligibleSelected,
            someEligibleSelected,
            eligibleCount: eligibleClients.length,
        }),
        [selectedIds, toggleClient, toggleSelectAll, isClientBillable, allEligibleSelected, someEligibleSelected, eligibleClients.length]
    );

    const getMonthInvoiceCount = useCallback((monthIndex) => {
        if (monthlyInvoiceCounts === null) return null;
        return monthlyInvoiceCounts[monthIndex] || 0;
    }, [monthlyInvoiceCounts]);

    const getMonthInvoiceAmount = useCallback((monthIndex) => {
        if (yearlyInvoiceStats === null) return 0;
        const monthData = yearlyInvoiceStats.monthly.find((m) => m.month === monthIndex + 1);
        return monthData ? monthData.total_ttc : 0;
    }, [yearlyInvoiceStats]);

    const totalAmount = useMemo(() => {
        return data.reduce((total, item) => {
            if (item.hasInvoice) {
                return total + (parseFloat(item.amountTTC) || 0);
            }
            return total;
        }, 0);
    }, [data]);

    const statusLabel = useMemo(() => {
        if (isBillable) return { text: `Facturable maintenant (consommation de ${consumptionMonth.format("MMMM YYYY")})`, className: "credit" };
        if (isExpired) return { text: `Période de facturation expirée`, className: "deactivated" };
        return { text: `Facturable à partir de ${billingMonth.format("MMMM YYYY")}`, className: "waiting" };
    }, [isBillable, isExpired, consumptionMonth, billingMonth]);

    const batchIntro = "Chaque hôtel reçoit une facture Pennylane. Les prélèvements sont à programmer ensuite dans Pennylane (Compte Pro > Prélèvements entrants).";

    const debitList = useMemo(() => buildDebitList(batchClients, batchStatus), [batchClients, batchStatus]);

    const copyDebitList = useCallback(() => {
        navigator.clipboard
            .writeText(formatDebitListForCopy(debitList))
            .then(() => toast.success("Liste copiée", toastOptions))
            .catch(() => toast.error("Impossible de copier la liste", toastOptions));
    }, [debitList]);

    const renderProgressStatus = useCallback((client) => {
        const state = batchStatus[client.id]?.status || "pending";
        switch (state) {
            case "success":
                return (
                    <span className="invoice-batch__st invoice-batch__st--ok">
                        <CheckIcon size={16} weight="bold" />
                        {batchStatus[client.id]?.result?.status === "recovered" ? "Facture rattrapée : vérifier l'envoi dans Pennylane" : "Facturé"}
                    </span>
                );
            case "processing":
                return (
                    <span className="invoice-batch__st invoice-batch__st--run">
                        <CircularProgress size={14} thickness={6} className="processing-spinner" />
                        Facturation en cours…
                    </span>
                );
            case "error":
                return (
                    <span className="invoice-batch__st invoice-batch__st--err">
                        <XIcon size={16} weight="bold" />
                        {batchStatus[client.id]?.error || "Échec"}
                    </span>
                );
            default:
                return (
                    <span className="invoice-batch__st invoice-batch__st--pending">
                        <ClockIcon size={16} weight="bold" />
                        En attente
                    </span>
                );
        }
    }, [batchStatus]);

    return isLoaded ? (
        <BillingSelectionContext.Provider value={selectionContextValue}>
        <div className="c-app__view">
            <CustomPageHeader
                title="Facturation métas"
                titleIcon={{
                    icon: GlobeIcon,
                }}
                badge={<CustomTag value={totalMetaClientsCount} />}
                customSelect={<YearSelector yearOptions={yearOptions} selectedYear={selectedYear} onYearChange={handleYearChange} isLoading={isDataLoading} />}
            />
            <CustomTag className={statusLabel.className} value={statusLabel.text} />
            <div className="tabs-container">
                <div className="fact-metas__tabs">
                    <Tabs
                        value={selectedMonth}
                        onChange={handleMonthChange}
                        variant="fullWidth"
                        style={{ flex: 1 }}
                    >
                        {constants.monthNames.map((_, index) => (
                            <Tab
                                key={index}
                                value={index}
                                label={
                                    <MonthTabLabel
                                        monthName={constants.monthAbbreviations[index]}
                                        invoiceCount={getMonthInvoiceCount(index)}
                                        year={selectedYear}
                                        invoiceAmount={getMonthInvoiceAmount(index)}
                                    />
                                }
                            />
                        ))}
                    </Tabs>
                    <div className="fact-metas__sum">
                        <div className="fact-metas__sum-container">
                            <span className="fact-metas__sum-title">Total facturé :</span>
                        </div>
                        <span className="fact-metas__sum-text">{formatAmount(totalAmount, 2)} € TTC</span>
                    </div>
                </div>

                {isBillable && (
                    <div className={`billing-import-zone ${!isImportExpanded ? "billing-import-zone--collapsed" : ""}`}>
                        <div className="billing-import-zone__header" onClick={() => setIsImportExpanded((prev) => !prev)}>
                            <div className="billing-import-zone__header-icon">
                                <UploadSimple size={16} weight="bold" />
                            </div>
                            <span>Import des montants</span>
                            <div className={`billing-import-zone__toggle ${!isImportExpanded ? "billing-import-zone__toggle--collapsed" : ""}`}>
                                <CaretDown size={16} weight="bold" />
                            </div>
                        </div>
                        <div className="billing-import-zone__body">
                            <div className="billing-import-zone__steps">
                                <div className="billing-import-zone__step">
                                    <div className="billing-import-zone__step-number">1</div>
                                    <div className="billing-import-zone__step-content">
                                        <span className="billing-import-zone__step-label">Récupérer le template</span>
                                        <button className="c-badge hover" onClick={handleDownloadTemplate}>
                                            <DownloadSimple size={14} weight="bold" />
                                            <span>Télécharger</span>
                                        </button>
                                    </div>
                                </div>
                                <div className="billing-import-zone__step">
                                    <div className="billing-import-zone__step-number">2</div>
                                    <div className="billing-import-zone__step-content">
                                        <span className="billing-import-zone__step-label">Importer le fichier complété</span>
                                        <CsvImporter
                                            label="Importer CSV"
                                            onImport={handleCsvImport}
                                            requiredColumns={REQUIRED_CSV_COLUMNS}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {isMonthLoading ? (
                    <div className="loader-container">
                        <CircularProgress size={30} />
                    </div>
                ) : (
                    <>
                        <CustomList
                            entity={data}
                            isSelectable={false}
                            isEditable={false}
                            isFetchable={false}
                            isClickable={false}
                            columns={columns}
                            setData={setData}
                            initialSortBy={currentSortBy}
                            onSortChange={setCurrentSortBy}
                        />
                        {isBillable && eligibleClients.length > 0 && (
                            <div className="fact-metas__actionbar">
                                <span className="fact-metas__actionbar-count">
                                    <b>{selectedIds.size}</b> hôtel(s) sélectionné(s)
                                </span>
                                <span className="fact-metas__actionbar-total">
                                    Total sélectionné <b>{formatAmount(selectedTotal, 2)} € TTC</b>
                                </span>
                                <div className="fact-metas__actionbar-spacer" />
                                <button className="button-large green-button" disabled={selectedIds.size === 0} onClick={openRecap}>
                                    <PaperPlaneTiltIcon size={16} weight="bold" />
                                    <span>Facturer la sélection{selectedIds.size > 0 ? ` (${selectedIds.size})` : ""}</span>
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Batch invoice modal: recap then live progress */}
            <CustomModal show={recapModalOpen} setShow={closeRecap} title={showProgress ? "Facturation" : `Facturer ${selectedClients.length} hôtel(s) - ${constants.monthNames[selectedMonth]} ${selectedYear}`} size="wide">
                {!showProgress ? (
                    <div className="invoice-batch">
                        <p className="invoice-batch__intro">{batchIntro}</p>
                        <div className="invoice-batch__list">
                            {selectedClients.map((client) => (
                                <div className="invoice-batch__row" key={client.id}>
                                    <span className="invoice-batch__name">{client.contact?.data?.customer_name}</span>
                                    <CustomTag value={client.taxId} />
                                    <CustomTag className={getPaymentModeTag(client.paymentMethod).className} value={getPaymentModeTag(client.paymentMethod).label} />
                                    <span className="invoice-batch__amount">{formatAmount(client.billingCost, 2)} €</span>
                                </div>
                            ))}
                        </div>
                        <div className="invoice-batch__total">
                            <span className="invoice-batch__total-label">Total à facturer</span>
                            <span className="invoice-batch__total-value">{formatAmount(selectedTotal, 2)} € TTC</span>
                        </div>
                    </div>
                ) : (
                    <div className="invoice-batch">
                        <div className="invoice-batch__progress-head">
                            <span className="invoice-batch__progress-title">{isBatchRunning ? "Facturation en cours" : "Facturation terminée"}</span>
                            <div className="invoice-batch__bar">
                                <i style={{ width: `${batchClients.length ? (doneCount / batchClients.length) * 100 : 0}%` }} />
                            </div>
                            <span className="invoice-batch__progress-count">
                                {doneCount} / {batchClients.length} traités
                            </span>
                        </div>
                        <div className="invoice-batch__list">
                            {batchClients.map((client) => (
                                <div className="invoice-batch__prog-row" key={client.id}>
                                    <span className="invoice-batch__name">{client.contact?.data?.customer_name}</span>
                                    {renderProgressStatus(client)}
                                </div>
                            ))}
                        </div>
                        {batchDone && debitList.length > 0 && (
                            <div className="invoice-batch__debits">
                                <div className="invoice-batch__debits-head">
                                    <span className="invoice-batch__progress-title">À prélever dans Pennylane ({debitList.length})</span>
                                    <button onClick={copyDebitList} className="button-large small-button">
                                        <CopyIcon size={14} weight="bold" />
                                        <span>Copier la liste</span>
                                    </button>
                                </div>
                                <div className="invoice-batch__list">
                                    {debitList.map((row) => (
                                        <div className="invoice-batch__row" key={row.invoiceNumber}>
                                            <span className="invoice-batch__name">{row.name}</span>
                                            <span>{row.invoiceNumber}</span>
                                            <span className="invoice-batch__amount">{formatAmount(row.amountTTC, 2)} €</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
                <div className="modal-buttons">
                    {!showProgress ? (
                        <>
                            <button onClick={closeRecap} className="button-large cancel-button">
                                Annuler
                            </button>
                            <button onClick={handleBatchInvoice} className="button-large green-button" disabled={selectedClients.length === 0}>
                                <PaperPlaneTiltIcon size={16} weight="bold" />
                                <span>Confirmer et facturer ({selectedClients.length})</span>
                            </button>
                        </>
                    ) : (
                        <button onClick={closeRecap} className="button-large green-button" disabled={isBatchRunning}>
                            {isBatchRunning ? <CircularProgress size={16} thickness={6} style={{ color: "white" }} /> : <span>Fermer</span>}
                        </button>
                    )}
                </div>
            </CustomModal>

            {/* Import preview modal */}
            <CustomModal show={importPreviewOpen} setShow={setImportPreviewOpen} title="Prévisualisation de l'import" size="wide">
                {importPreviewData && (
                    <div className="import-preview">
                        <div className="import-preview__summary">
                            <div className="import-preview__stat-card import-preview__stat-card--success">
                                <span className="import-preview__stat-value">{importPreviewData.matched.length}</span>
                                <span className="import-preview__stat-label">Clients matchés</span>
                            </div>
                            {importPreviewData.unmatched.length > 0 && (
                                <div className="import-preview__stat-card import-preview__stat-card--warning">
                                    <span className="import-preview__stat-value">{importPreviewData.unmatched.length}</span>
                                    <span className="import-preview__stat-label">Non trouvés</span>
                                </div>
                            )}
                            {importPreviewData.emptyAmount.length > 0 && (
                                <div className="import-preview__stat-card import-preview__stat-card--neutral">
                                    <span className="import-preview__stat-value">{importPreviewData.emptyAmount.length}</span>
                                    <span className="import-preview__stat-label">Montants vides</span>
                                </div>
                            )}
                            {importPreviewData.missingFromCsv.length > 0 && (
                                <div className="import-preview__stat-card import-preview__stat-card--neutral">
                                    <span className="import-preview__stat-value">{importPreviewData.missingFromCsv.length}</span>
                                    <span className="import-preview__stat-label">Absents du CSV</span>
                                </div>
                            )}
                        </div>

                        {importPreviewData.matched.length > 0 && (
                            <div className="import-preview__section">
                                <h4 className="import-preview__section-title">
                                    <CheckIcon size={16} weight="bold" color="var(--green-500)" />
                                    Montants à importer
                                </h4>
                                <div className="import-preview__table-wrapper">
                                    <table className="import-preview__table">
                                        <thead>
                                            <tr>
                                                <th>Engine Code</th>
                                                <th>Hôtel</th>
                                                <th>Montant TTC</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {importPreviewData.matched.map((item) => (
                                                <tr key={item.engineCode}>
                                                    <td className="import-preview__engine-code">{item.engineCode}</td>
                                                    <td>{item.hotel}</td>
                                                    <td className="import-preview__amount">{formatAmount(item.amountTTC, 2)} €</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {importPreviewData.unmatched.length > 0 && (
                            <div className="import-preview__section import-preview__section--warning">
                                <h4 className="import-preview__section-title">
                                    <WarningCircle size={16} weight="bold" color="var(--orange-500)" />
                                    EngineCode non trouvés (ignorés)
                                </h4>
                                <div className="import-preview__tags">
                                    {importPreviewData.unmatched.map((item) => (
                                        <CustomTag key={item.engineCode} className="waiting" value={`${item.engineCode} - ${item.hotel}`} />
                                    ))}
                                </div>
                            </div>
                        )}

                        {importPreviewData.missingFromCsv.length > 0 && (
                            <div className="import-preview__section import-preview__section--info">
                                <h4 className="import-preview__section-title">
                                    <WarningCircle size={16} weight="bold" color="var(--black-30)" />
                                    Clients absents du CSV (inchangés)
                                </h4>
                                <div className="import-preview__tags">
                                    {importPreviewData.missingFromCsv.map((item) => (
                                        <CustomTag key={item.engineCode} className="deactivated" value={`${item.engineCode} - ${item.hotel}`} />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
                <div className="modal-buttons">
                    <button onClick={() => setImportPreviewOpen(false)} className="button-large cancel-button" disabled={isImporting}>
                        Annuler
                    </button>
                    <button
                        onClick={handleConfirmImport}
                        className="button-large green-button"
                        disabled={isImporting || !importPreviewData?.matched?.length}
                    >
                        {isImporting ? (
                            <CircularProgress size={16} thickness={6} style={{ color: "white" }} />
                        ) : (
                            <>
                                <UploadSimple size={16} weight="bold" />
                                <span>Confirmer l'import ({importPreviewData?.matched?.length || 0})</span>
                            </>
                        )}
                    </button>
                </div>
            </CustomModal>
        </div>
        </BillingSelectionContext.Provider>
    ) : (
        <div className="loader-container absolute">
            <CircularProgress size={30} />
        </div>
    );
};

export default Billing;
